import { Folder } from '@anm/api/modules/videoFolder';
import selectProps from '@anm/helpers/store/selectProps';
import { createSelector } from 'reselect';
import { AnimatronProject } from 'types/project';

import { ApplicationState } from '../../../types';
import { selectSearchProjectsList } from '../../project/selectors';
import { SelectProjectProps } from '../types';

import { findFolder, getFolderTreeWithout, getProjectPreview } from './../helpers';

export const selectFoldersAndProjects = ({ projects, folders }: Folder) => [...folders, ...projects];

export const selectVideoFolders = ({ videoFolder }: ApplicationState) => videoFolder;

export const selectCurrentFolder = ({ videoFolder }: ApplicationState) => videoFolder.data;
export const selectVideoFoldersList = createSelector(selectCurrentFolder, folderData => folderData?.folders);

export const selectNewFolderData = (state: ApplicationState) => selectVideoFolders(state).newFolder.data;

export const selectRenameFolderState = (state: ApplicationState) => selectVideoFolders(state).renameFolder;

export const selectCurrentFolderError = ({ videoFolder }: ApplicationState) => videoFolder.error;

export const selectCurrentFolderPendingStatus = ({ videoFolder }: ApplicationState) => videoFolder.isPending;

export const selectDuplicateProject = createSelector(selectVideoFolders, videoFolders => videoFolders.duplicateProject);

export const selectFolderDataById = createSelector(
  selectVideoFoldersList,
  selectProps<number>(),
  (folderList, folderId) => folderList?.find(({ id }) => id === folderId)
);

export const selectMoveProjectToOtherWorkspace = createSelector(
  selectVideoFolders,
  videoFolders => videoFolders.moveProjectToOtherWorkspace
);
export const selectMoveProjectToOtherWorkspacePending = createSelector(
  selectMoveProjectToOtherWorkspace,
  selectProps<string>(),
  ({ params, isPending }, projectId) => params?.projectId === projectId && isPending
);

export const selectProjectDuplicateStatus = createSelector(
  selectDuplicateProject,
  selectProps<string>(),
  (duplicateProject, projectId) => duplicateProject.params?.projectId === projectId && duplicateProject.isPending
);

export const selectCurrentFolderId = ({ videoFolder }: ApplicationState) =>
  videoFolder?.data ? videoFolder.data.id : null;

export const isRootFolder = (state: ApplicationState) => {
  const currentFolder = selectCurrentFolder(state);
  return currentFolder && !currentFolder.parentId;
};
export const checkCurrentFolderByPath = (state: ApplicationState) => (folderPath: string) => {
  const currentFolder = selectCurrentFolder(state);
  if (!currentFolder) return false;

  return folderPath === makeFolderPath(state)(currentFolder.id);
};

export const selectFolderTree = ({ videoFolder }: ApplicationState) => videoFolder.folderTree.data!;
export const selectFolderTreePending = ({ videoFolder }: ApplicationState) => videoFolder.folderTree.isPending;

export const selectCurrentFolders = ({ videoFolder }: ApplicationState) => videoFolder.data?.folders;

export const selectCurrentProjects = ({ videoFolder }: ApplicationState) => videoFolder?.data?.projects;

export const selectProjectById = createSelector(
  selectCurrentProjects,
  selectSearchProjectsList,
  selectProps<string>(),
  (projects, searchProjects, projectId) => {
    const search = (project: AnimatronProject) => project.id === projectId;
    return projects?.find(search) || searchProjects?.find(search);
  }
);

export const selectNestedProjectByFolderId = createSelector(
  selectCurrentFolder,
  selectProps<SelectProjectProps>(),
  (parentFolder, { folderId, projectId }) => {
    const folder = parentFolder ? findFolder(parentFolder, folderId) : null;
    const project = folder?.projects.find(p => p.id === projectId);

    return project;
  }
);

export const selectSmallestProjectPreview = createSelector(
  selectProps<string>(),
  selectProjectById,
  (projectId, project) =>
    getProjectPreview(
      (prevSize: number, currentSize: number) => !!(!prevSize || (currentSize && currentSize < prevSize)),
      projectId,
      project
    )
);

export const selectProjectPreviewBySize = createSelector(
  selectProjectById,
  (_, projectId, retinaSize: number) => ({ projectId, retinaSize }),
  (project, { projectId, retinaSize }) =>
    getProjectPreview(
      (_: number, currentSize: number) => !!(currentSize && retinaSize === currentSize),
      projectId,
      project
    )
);

export const selectFolderTreeByPath = ({ videoFolder }: ApplicationState) => (path: string) => {
  const folderTree = videoFolder.folderTree.data!;

  let slugs = path.split('/');
  const find = (parentFolder: Folder): undefined | Folder => {
    const currentSlug = slugs[0];

    parentFolder.folders.forEach(folder => {
      if (folder.slug === currentSlug) {
        slugs = slugs.slice(1);
        find(folder);
        parentFolder.folder = folder;
      }
    });

    return parentFolder;
  };

  return find(folderTree)!;
};

export const makeFolderTreeListByPath = (state: ApplicationState) => (path: string, isOnlyRoot = false) => {
  const folderTree = selectFolderTreeByPath(state)(path);
  const list: Folder[] = [];

  const rootPath = '/root-wave';
  let slugPath = '';
  const makeList = (folder: Folder) => {
    if (!folder) return;

    const isRoot = !folder.parentId;
    slugPath = isRoot ? '' : `${slugPath}/${folder.slug}`;
    folder.path = slugPath;

    list.push(folder);

    const ownFolder = folder.folder;

    if (ownFolder && `/${path}` !== slugPath && `/${path}` !== rootPath) {
      makeList(ownFolder);
    }
  };

  const isNotRoot = path !== folderTree.slug;
  if (isNotRoot || isOnlyRoot) {
    makeList(folderTree);
  }

  return list;
};

export const findFolderInTree = (state: ApplicationState) => (folderId: number | null) => {
  const folderTree = selectFolderTree(state);

  const find = (parentFolder?: Folder) => {
    let result;
    if (parentFolder?.id === folderId || !folderId) return parentFolder;

    parentFolder?.folders.some(folder => {
      result = find(folder);
      return !!result;
    });

    return result;
  };

  return find(folderTree);
};

export const selectFolderTreeWithout = <P extends number | undefined>(folderId: P) => (state: ApplicationState) =>
  createSelector(selectFolderTree, selectProps<P>(), (folderTree, folderId) =>
    getFolderTreeWithout(folderTree)(folderId)
  )(state, folderId);

export const isFolderEmpty = (state: ApplicationState) => (folderId: number) => {
  const folder = findFolderInTree(state)(folderId);
  if (!folder) throw new Error(`folder: ${folderId} - not found`);

  return (!folder.projectsCount || folder.projectsCount === 0) && folder.folders.length === 0;
};

export const isFolderExist = (state: ApplicationState) => (folderId: number) => !!findFolderInTree(state)(folderId);

export const makeFolderPath = (state: ApplicationState) => (folderId: number) => {
  const folderTree = selectFolderTree(state);

  if (!folderTree) return;

  const find = (folder: Folder, acc: Folder[] = []) => {
    const isRootFolder = !folder.parentId;

    if (folderId === folder.id) {
      acc.unshift(folder);
      return { acc, isFound: true };
    }

    const isFound = folder.folders.some(v => find(v, acc).isFound);

    if (isFound && !isRootFolder) {
      acc.unshift(folder);
    }

    return { acc, isFound };
  };

  const path = find(folderTree).acc.reduce((acc, v) => `${acc && `${acc}/`}${v.slug}`, '');

  return path;
};

export const getFolderPathFor = createSelector(
  selectFolderTree,
  selectProps<number | undefined>(),
  (folderTree, folderId) => {
    if (!folderTree) return;

    const find = (folder: Folder, acc: Folder[] = []) => {
      const isRootFolder = !folder.parentId;
      if (folderId === folder.id) {
        acc.unshift(folder);
        return { acc, isFound: true };
      }

      const isFound = folder.folders.some(v => find(v, acc).isFound);

      if (isFound && !isRootFolder) {
        acc.unshift(folder);
      }

      return { acc, isFound };
    };

    const path = find(folderTree).acc.reduce((acc, v) => `${acc && `${acc}/`}${v.slug}`, '');
    return path;
  }
);

export const selectNewFolderFor = createSelector(selectNewFolderData, selectProps<number>(), (newFolder, folderId) =>
  newFolder?.id === folderId ? newFolder : null
);

export const selectRenameFolderFor = createSelector(
  selectRenameFolderState,
  selectProps<number>(),
  (renameFolder, folderId) => (renameFolder?.params?.folderId === folderId ? renameFolder : null)
);

export const selectUpdateProjectMeta = ({ videoFolder }: ApplicationState) => videoFolder.updateProjectMeta;

export const selectCreatedProject = ({ videoFolder }: ApplicationState) => videoFolder.createProject;

export const selectCreatedProjectError = ({ videoFolder }: ApplicationState) => videoFolder.createProject.isError;

export const selectCreatedProjectId = ({ videoFolder }: ApplicationState) => videoFolder.createProject.data?.id;

export const selectCreatedProjectPending = ({ videoFolder }: ApplicationState) => videoFolder.createProject.isPending;

export const selectDeleteProjectIsPending = createSelector(
  selectVideoFolders,
  videoFolders => videoFolders.deleteProject.isPending
);

export const selectProjectByHostId = createSelector(selectCurrentProjects, selectProps<string>(), (projects, hostId) =>
  projects?.find(p => p.extra.hostingId === hostId)
);

export const selectDeleteProject = createSelector(selectVideoFolders, videoFolders => videoFolders.deleteProject);
